import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import emailjs from "emailjs-com";
import { useGetProductDetailsQuery } from "../../redux/api/productsApi";

const DEFAULT_FORM_VALUES = {
  name: "",
  email: "",
  phone: "",
  company: "",
  message: "",
  products: [{ partNumber: "", qty: "", targetPrice: "" }],
};

/**
 * 普通商品询价表单组件：负责收集料号、数量、目标价并通过 EmailJS 发送给业务邮箱。
 */
function FormRequestQuote() {
  const [searchParams] = useSearchParams();
  const productId = searchParams.get("productId") || "";
  const quoteQty = searchParams.get("qty") || "";
  const { data: productDetails } = useGetProductDetailsQuery(productId, {
    skip: !productId,
  });
  const [formValues, setFormValues] = useState(DEFAULT_FORM_VALUES);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const product = productDetails?.product;

    if (!product) {
      return;
    }

    const partNumber =
      product.Manufacturer_Part_Number ||
      product.CTYEA_Part_Number ||
      product.name ||
      "";

    setFormValues((prevState) => ({
      ...prevState,
      products: [
        {
          ...prevState.products[0],
          partNumber: prevState.products[0].partNumber || partNumber,
          qty: prevState.products[0].qty || quoteQty,
        },
        ...prevState.products.slice(1),
      ],
    }));
  }, [productDetails, quoteQty]);

  /**
   * 处理客户信息和商品明细字段输入。
   * @param {React.ChangeEvent<HTMLInputElement|HTMLTextAreaElement>} e 输入事件
   * @param {number} [index] 商品行索引
   */
  const handleInputChange = (e, index) => {
    const { name, value } = e.target;

    if (name.startsWith("product_")) {
      const updatedProducts = [...formValues.products];
      const field = name.split("_")[1];
      updatedProducts[index][field] = value;
      setFormValues({ ...formValues, products: updatedProducts });
      return;
    }

    setFormValues({ ...formValues, [name]: value });
  };

  /**
   * 通过 EmailJS 提交普通商品询价。
   * @param {React.FormEvent<HTMLFormElement>} e 表单提交事件
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await emailjs.send(
        "service_q63dw1a",
        "template_pc39adm",
        formValues,
        "d7avdztHAtJLl4van"
      );
      toast.success("Quote request submitted successfully.");
      setFormValues(DEFAULT_FORM_VALUES);
    } catch (error) {
      toast.error("Failed to submit quote request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * 新增一个商品询价行。
   */
  const addProductRow = () => {
    const newProduct = { partNumber: "", qty: "", targetPrice: "" };
    setFormValues({
      ...formValues,
      products: [...formValues.products, newProduct],
    });
  };

  /**
   * 移除指定商品询价行。
   * @param {number} index 商品行索引
   */
  const removeProductRow = (index) => {
    if (formValues.products.length === 1) {
      return;
    }

    const filteredProducts = formValues.products.filter((_, i) => i !== index);
    setFormValues({ ...formValues, products: filteredProducts });
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-4xl mx-auto p-4">
      <div className="mb-4">
        <label
          htmlFor="name"
          className="block text-sm font-semibold text-gray-700"
        >
          Name *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formValues.name}
          onChange={handleInputChange}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          required
        />
      </div>
      <div className="mb-4">
        <label
          htmlFor="email"
          className="block text-sm font-semibold text-gray-700"
        >
          Email *
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formValues.email}
          onChange={handleInputChange}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          required
        />
      </div>
      <div className="mb-4">
        <label
          htmlFor="phone"
          className="block text-sm font-semibold text-gray-700"
        >
          Phone
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formValues.phone}
          onChange={handleInputChange}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>
      <div className="mb-4">
        <label
          htmlFor="company"
          className="block text-sm font-semibold text-gray-700"
        >
          Company
        </label>
        <input
          type="text"
          id="company"
          name="company"
          value={formValues.company}
          onChange={handleInputChange}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>

      <div>
        <h4 className="font-semibold">Products Required *</h4>
        {formValues.products.map((product, index) => (
          <div
            key={index}
            className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end mb-4"
          >
            <div>
              <label htmlFor={`product_partNumber_${index}`}>
                Part Number:
              </label>
              <input
                type="text"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                id={`product_partNumber_${index}`}
                name={`product_partNumber_${index}`}
                value={product.partNumber}
                onChange={(e) => handleInputChange(e, index)}
                required
              />
            </div>
            <div>
              <label htmlFor={`product_qty_${index}`}>Qty Needed:</label>
              <input
                type="number"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                id={`product_qty_${index}`}
                name={`product_qty_${index}`}
                value={product.qty}
                onChange={(e) => handleInputChange(e, index)}
                required
              />
            </div>
            <div>
              <label htmlFor={`product_targetPrice_${index}`}>
                Target Price ($):
              </label>
              <input
                type="number"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                id={`product_targetPrice_${index}`}
                name={`product_targetPrice_${index}`}
                value={product.targetPrice}
                onChange={(e) => handleInputChange(e, index)}
                required
              />
            </div>
            <div className="col-span-full">
              <button
                type="button"
                className="mt-2 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded disabled:bg-gray-300"
                onClick={() => removeProductRow(index)}
                disabled={formValues.products.length === 1}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
          onClick={addProductRow}
        >
          Add Item To Quote
        </button>
      </div>

      <div className="mt-6">
        <label
          htmlFor="message"
          className="block text-sm font-semibold text-gray-700"
        >
          Message *
        </label>
        <textarea
          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          id="message"
          name="message"
          value={formValues.message}
          onChange={handleInputChange}
          required
        />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="mt-6 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded float-right disabled:bg-gray-400"
      >
        {isSubmitting ? "Submitting..." : "Submit"}
      </button>
    </form>
  );
}

export default FormRequestQuote;
